import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { Profile } from '../../../types/profile';
import type { AdminUserActivity } from '../../../types/admin_user_activity';
import { formatDateLocalized } from '../../../utils/dateUtils';
import { LoadingSpinner } from '../../LoadingSpinner';

interface AdminUserActivitySectionProps {
  role: Profile['role'];
  profileId: string;
  activity: AdminUserActivity;
  loading: boolean;
  error: string | null;
}

interface ActivityRow {
  id: string;
  title: string;
  status: string;
  statusGroup: 'job_statuses' | 'quote_statuses';
  date: string;
  href: string;
  detail?: string;
}

const ActivityGroup = ({ title, rows }: { title: string; rows: ActivityRow[] }) => {
  const { t, i18n } = useTranslation(['admin', 'common']);

  return (
    <section className="rounded-xl border border-gray-200 bg-gray-50 p-4">
      <h4 className="mb-3 font-semibold text-secondary">{title}</h4>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">{t('userActivity.empty', { ns: 'admin' })}</p>
      ) : (
        <div className="space-y-2">
          {rows.map(row => (
            <Link
              key={row.id}
              to={row.href}
              className="flex items-center justify-between gap-4 rounded-lg bg-white px-3 py-2 shadow-sm transition-colors hover:bg-primary/5"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-secondary">{row.title}</p>
                <p className="text-xs text-gray-500">
                  {t(`${row.statusGroup}.${row.status}`, {
                    ns: 'common',
                    defaultValue: row.status,
                  })}
                  {row.detail ? ` · ${row.detail}` : ''}
                  {' · '}
                  {formatDateLocalized(row.date, i18n.language, {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </p>
              </div>
              <ExternalLink className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
            </Link>
          ))}
        </div>
      )}
    </section>
  );
};

const buildFreelancerHubHref = (
  jobId: string,
  principalQuoteId: string,
  profileId: string
): string =>
  `/admin/jobs/${jobId}/hub?adminPerspective=freelancer&adminPrincipalQuoteId=${principalQuoteId}&adminEffectiveViewerId=${profileId}`;

export default function AdminUserActivitySection({
  role,
  profileId,
  activity,
  loading,
  error,
}: AdminUserActivitySectionProps) {
  const { t } = useTranslation('admin');

  if (role === 'administrator') return null;

  if (loading) {
    return (
      <div className="flex min-h-24 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <p className="rounded-xl bg-red-50 p-4 text-sm text-red-700">
        {t(error, { defaultValue: 'Could not load this user activity.' })}
      </p>
    );
  }

  const quoteRows: ActivityRow[] = activity.quotes.map(quote => ({
    id: quote.id,
    title: quote.job_title,
    status: quote.quote_status,
    statusGroup: 'quote_statuses',
    date: quote.submitted_at ?? quote.created_at,
    href: buildFreelancerHubHref(quote.job_id, quote.principal_quote_id, profileId),
  }));

  const projectRows: ActivityRow[] = activity.projects.map(project => ({
    id: project.id,
    title: project.job_title,
    status: project.job_status,
    statusGroup: 'job_statuses',
    date: project.completed_at ?? project.created_at,
    href: buildFreelancerHubHref(project.job_id, project.principal_quote_id, profileId),
  }));

  const jobRows: ActivityRow[] = activity.jobs.map(job => ({
    id: job.id,
    title: job.title,
    status: job.status,
    statusGroup: 'job_statuses',
    date: job.published_at ?? job.created_at,
    href: `/admin/jobs/${job.job_id}/hub`,
    detail: t('userActivity.quotesCount', { count: job.quotes_count }),
  }));

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500">
        {t('userActivity.title')}
      </h3>
      {role === 'freelancer' ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ActivityGroup title={t('userActivity.quotes')} rows={quoteRows} />
          <ActivityGroup title={t('userActivity.projects')} rows={projectRows} />
        </div>
      ) : (
        <ActivityGroup title={t('userActivity.jobs')} rows={jobRows} />
      )}
    </div>
  );
}
